import React, { useState, useMemo } from 'react';

import addMonths from 'date-fns/addMonths';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import { connect } from 'react-redux';

import classes from '@nerdwallet/base-styles/classes';
import { Link, Table, Box } from '@nerdwallet/currency';
import { formatCurrency } from '@nerdwallet/nw-client-lib/num';

import {
  inputsSelector,
  manualInputsSelector,
} from '../../../../../lib/selectors/amortization-calculator-selector';
import type { ScheduleItem, Browser } from '../../../../../lib/types';

interface ScheduleProps {
  browser: Browser;
  monthlySchedule: Array<ScheduleItem>;
  manualInputs: Record<string, any>;
  inputs: Record<string, any>;
}

const INITIAL_AMOUNT_OF_ROWS_TO_SHOW = 7;

function getTableData(
  monthlySchedule: Array<ScheduleItem>,
  isMobile: boolean,
  amountOfRowsToShow: number,
  loanStartDate: string
) {
  const parsedDate = parse(loanStartDate, 'MM / dd / yyyy', new Date());
  const startDate = loanStartDate ? parsedDate : new Date();
  const validDate = isValid(startDate);
  if (isMobile) {
    return monthlySchedule
      ?.slice(0, amountOfRowsToShow)
      .map((month, index) => ({
        paymentDate: validDate
          ? format(addMonths(startDate, index), "MMMM ''yy")
          : 'Invalid Date',
        balance: formatCurrency(month?.remaining),
      }));
  }

  return monthlySchedule?.map((month, index) => ({
    paymentDate: validDate
      ? format(addMonths(startDate, index), "MMMM ''yy")
      : 'Invalid Date',
    principal: formatCurrency(month?.payment?.principal),
    interest: formatCurrency(month?.payment?.interest),
    balance: formatCurrency(month?.remaining),
  }));
}

const Schedule: React.FC<ScheduleProps> = ({
  browser,
  monthlySchedule,
  manualInputs,
  inputs,
}) => {
  // this will only impact mobile
  const [amountOfRowsToShow, setAmountOfRowsToShow] = useState(
    INITIAL_AMOUNT_OF_ROWS_TO_SHOW
  );

  const onExpand = () => {
    setAmountOfRowsToShow(
      (currentAmountOfRowsToShow) =>
        currentAmountOfRowsToShow + INITIAL_AMOUNT_OF_ROWS_TO_SHOW
    );
  };

  const isMobile = browser?.isMobile;

  const columns = useMemo(() => {
    if (isMobile) {
      return [
        { heading: 'Payment date', id: 'paymentDate' },
        { heading: 'Balance', id: 'balance' },
      ];
    }

    return [
      { heading: 'Payment date', id: 'paymentDate' },
      { heading: 'Principal', id: 'principal' },
      { heading: 'Interest', id: 'interest' },
      { heading: 'Balance', id: 'balance' },
    ];
  }, [isMobile]);

  const monthlyScheduleJSON = JSON.stringify(monthlySchedule);

  const loanStartDate = manualInputs?.loanStartDate || inputs?.loanStartDate;
  const data = useMemo(() => {
    return getTableData(
      monthlySchedule,
      isMobile,
      amountOfRowsToShow,
      loanStartDate
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, monthlyScheduleJSON, amountOfRowsToShow, loanStartDate]);

  return (
    <Box>
      <Table columns={columns} data={data} fixedHeader maxHeight={504} />
      <Box className={classes('display-none--wide')} paddingTop={2}>
        <Link onClick={onExpand}>See More</Link>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  inputs: inputsSelector(state),
  manualInputs: manualInputsSelector(state),
});

export default connect(mapStateToProps)(Schedule);
